<template>
  <div class="form-container">
    <h1 class="mb-4">顧客情報新規作成</h1>
    <form @submit.prevent="handleSubmit" @keydown.enter.prevent>

      <div class="mb-3">
        <label for="customerName" class="form-label">顧客名 <span class="badge text-bg-danger">必須</span></label>
        <input v-model="customer.customer_nm" type="text" placeholder="顧客名" class="form-control" id="customerName" required>
        <div v-if="errorMessages.customer_nm" class="alert alert-danger mt-2">{{ errorMessages.customer_nm[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="customerNo" class="form-label">顧客番号</label>
        <input v-model="customer.customer_no" type="text" placeholder="顧客番号" class="form-control" id="customerNo">
        <div v-if="errorMessages.customer_no" class="alert alert-danger mt-2">{{ errorMessages.customer_no[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="postalCode" class="form-label">郵便番号</label>
        <input v-model="customer.postal_code" type="text" placeholder="郵便番号" class="form-control" id="postalCode">
        <div v-if="errorMessages.postal_code" class="alert alert-danger mt-2">{{ errorMessages.postal_code[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="prefecture" class="form-label">都道府県</label>
        <input v-model="customer.prefecture" type="text" placeholder="都道府県" class="form-control" id="prefecture">
        <div v-if="errorMessages.prefecture" class="alert alert-danger mt-2">{{ errorMessages.prefecture[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="city" class="form-label">市区町村</label>
        <input v-model="customer.city" type="text" placeholder="市区町村" class="form-control" id="city">
        <div v-if="errorMessages.city" class="alert alert-danger mt-2">{{ errorMessages.city[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="otherAddress" class="form-label">その他の住所</label>
        <input v-model="customer.other_address" type="text" placeholder="その他の住所" class="form-control" id="otherAddress">
        <div v-if="errorMessages.other_address" class="alert alert-danger mt-2">{{ errorMessages.other_address[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="telNum" class="form-label">電話番号</label>
        <input v-model="customer.tel_num" type="text" placeholder="電話番号" class="form-control" id="telNum">
        <div v-if="errorMessages.tel_num" class="alert alert-danger mt-2">{{ errorMessages.tel_num[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="fax" class="form-label">FAX</label>
        <input v-model="customer.fax" type="text" placeholder="FAX" class="form-control" id="fax">
        <div v-if="errorMessages.fax" class="alert alert-danger mt-2">{{ errorMessages.fax[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="presidentNm" class="form-label">代表者名</label>
        <input v-model="customer.president_nm" type="text" placeholder="代表者名" class="form-control" id="presidentNm">
        <div v-if="errorMessages.president_nm" class="alert alert-danger mt-2">{{ errorMessages.president_nm[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="billingNm" class="form-label">請求先名</label>
        <input v-model="customer.billing_nm" type="text" placeholder="請求先名" class="form-control" id="billingNm">
        <div v-if="errorMessages.billing_nm" class="alert alert-danger mt-2">{{ errorMessages.billing_nm[0] }}</div>
      </div>

      <div class="btn-area">
        <a class="btn btn-secondary mx-1" @click="$router.back()">戻る</a>
        <button type="submit" class="btn btn-primary mx-1" :disabled="isSubmitting">登録</button>
      </div>
    </form>

    <DuplicateNameConfirmationModal
      :modalId="duplicateModalId"
      message="すでに同名の顧客名が登録されていますが、本当に作成してもよろしいでしょうか？"
      @confirm="handleDuplicateConfirm"
    />
  </div>
</template>

<script>
import { ref, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { Modal } from 'bootstrap';
import { checkCustomerDuplicate, createCustomer } from '@/api/axios';
import DuplicateNameConfirmationModal from '@/components/shared/DuplicateNameConfirmationModal.vue';

export default {
  components: {
    DuplicateNameConfirmationModal
  },
  setup() {
    const title = ref('顧客情報新規作成');

    watchEffect(() => {
      document.title = title.value;
    });

    const customer = ref({
      customer_nm: '',
      customer_no: '',
      postal_code: '',
      prefecture: '',
      city: '',
      other_address: '',
      tel_num: '',
      fax: '',
      president_nm: '',
      billing_nm: ''
      // 他のフィールド
    });

    // エラーメッセージ用のリアクティブプロパティ
    const errorMessages = ref({});
    const isSubmitting = ref(false);
    const duplicateModalId = 'duplicateCustomerCreateModal';

    const store = useStore();
    const router = useRouter();

    const showDuplicateModal = () => {
      const modalElement = document.getElementById(duplicateModalId);
      const modal = Modal.getInstance(modalElement) || new Modal(modalElement);
      modal.show();
    };

    const saveCustomer = async () => {
      if (isSubmitting.value) return;

      try {
        isSubmitting.value = true;
        await createCustomer(customer.value);
        store.commit('setFlashMessage', {message: '顧客情報作成完了！', type: 'success'});
        router.push(`/customers`);
      } catch (error) {
        if (error.response && error.response.data) {
          // エラーメッセージをerrorMessagesにセット
          errorMessages.value = error.response.data || {};
        }
        store.commit('setFlashMessage', {message: '顧客情報作成失敗！', type: 'danger'});
        console.error('作成エラー:', error);
      } finally {
        isSubmitting.value = false;
      }
    };

    const handleSubmit = async () => {
      if (isSubmitting.value) return;

      try {
        isSubmitting.value = true;
        const response = await checkCustomerDuplicate(customer.value.customer_nm);
        if (response.data?.duplicate) {
          showDuplicateModal();
          return;
        }
      } catch (error) {
        console.error('重複チェックエラー:', error);
      } finally {
        isSubmitting.value = false;
      }

      await saveCustomer();
    };

    const handleDuplicateConfirm = async () => {
      await saveCustomer();
    };

    return {
      title,
      customer,
      handleSubmit,
      handleDuplicateConfirm,
      errorMessages,
      isSubmitting,
      duplicateModalId
    };
  }
};
</script>

<style scoped>
.form-container {
  padding: 20px 20px 80px 20px;
  width: 70%;
  margin: auto;
  border: 1px solid #ddd;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

input[type="text"],
input[type="email"],
input[type="password"] {
  width: 100%;
  padding: 10px;
  margin-bottom: 4px;
  border: 1px solid #ddd;
  border-radius: 5px;
}

.btn-area{
  text-align: center;
}
</style>
